
import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowLeft, MailPlus, Phone, Calendar, Tag, Clock, Check, Mail, Loader2 } from 'lucide-react';
import { CRMUser } from '@/types/crm';
import { format } from 'date-fns';

interface CRMUserDetailProps {
  user: CRMUser;
  onBack: () => void;
  onSendReferral: (userId: string) => void;
  isProcessing: boolean;
}

const CRMUserDetail: React.FC<CRMUserDetailProps> = ({
  user,
  onBack,
  onSendReferral,
  isProcessing
}) => {
  // Helper to format date
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM d, yyyy h:mm a');
    } catch {
      return 'Invalid date';
    }
  };

  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'new':
        return <Badge className="bg-blue-50 text-blue-700 border-blue-200">New</Badge>;
      case 'contacted':
        return <Badge className="bg-yellow-50 text-yellow-700 border-yellow-200">Contacted</Badge>;
      case 'active':
        return <Badge className="bg-green-50 text-green-700 border-green-200">Active</Badge>;
      case 'inactive':
        return <Badge className="bg-gray-100 text-gray-700 border-gray-200">Inactive</Badge>;
      case 'converted':
        return <Badge className="bg-purple-50 text-purple-700 border-purple-200">Converted</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  // Get email status badge
  const getEmailStatusBadge = (status: string) => {
    switch (status) {
      case 'sent':
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Sent</Badge>;
      case 'delivered':
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">Delivered</Badge>;
      case 'opened':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Opened</Badge>;
      case 'failed':
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Failed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to list
        </Button>
        
        <Button
          onClick={() => onSendReferral(user.id)}
          disabled={isProcessing}
          className="flex items-center gap-2"
        >
          {isProcessing ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <MailPlus className="h-4 w-4" />
              Send Referral
            </>
          )}
        </Button>
      </div>
      
      <Card>
        <CardHeader className="pb-2">
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="text-xl">{user.name}</CardTitle>
              <CardDescription className="flex items-center gap-2 mt-1">
                <Mail className="h-4 w-4" />
                {user.email}
                {user.phone && (
                  <>
                    <span className="mx-1">•</span>
                    <Phone className="h-4 w-4" />
                    {user.phone}
                  </>
                )}
              </CardDescription>
            </div>
            {getStatusBadge(user.status)}
          </div>
        </CardHeader>
        
        <CardContent className="pt-4 pb-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-2 flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  User Details
                </h3>
                <dl className="grid grid-cols-2 gap-1 text-sm">
                  <dt className="text-muted-foreground">Added on</dt>
                  <dd>{formatDate(user.dateAdded)}</dd>
                  
                  <dt className="text-muted-foreground">Last contact</dt>
                  <dd>{user.lastContactDate ? formatDate(user.lastContactDate) : 'Never contacted'}</dd>
                  
                  <dt className="text-muted-foreground">Total referrals</dt>
                  <dd>{user.totalReferrals}</dd>
                </dl>
              </div>
              
              {user.tags && user.tags.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-2 flex items-center">
                    <Tag className="h-4 w-4 mr-1" />
                    Tags
                  </h3>
                  <div className="flex flex-wrap gap-1">
                    {user.tags.map((tag, i) => (
                      <Badge key={i} variant="secondary">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {user.notes && (
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-2">Notes</h3>
                  <p className="text-sm">{user.notes}</p>
                </div>
              )}
            </div>
            
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-2 flex items-center">
                <Clock className="h-4 w-4 mr-1" />
                Referral Email History
              </h3>
              
              {user.referralHistory.length > 0 ? (
                <div className="border rounded-md overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Template</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Sent</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {user.referralHistory.map((email) => (
                        <TableRow key={email.id}>
                          <TableCell>{email.templateName}</TableCell>
                          <TableCell>{getEmailStatusBadge(email.status)}</TableCell>
                          <TableCell>{formatDate(email.sentDate).split(' ')[0]}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-8 border rounded-md">
                  <p className="text-muted-foreground text-sm">No referral emails sent yet</p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
        
        <Separator className="my-2" />
        
        <CardFooter className="pt-4">
          <div className="flex justify-between w-full">
            <div className="flex items-center text-sm text-muted-foreground">
              <span>User ID: {user.id}</span>
            </div>
            
            <Button
              onClick={() => onSendReferral(user.id)}
              disabled={isProcessing}
              className="flex items-center gap-2"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <MailPlus className="h-4 w-4" />
                  Send Referral
                </>
              )}
            </Button>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
};

export default CRMUserDetail;
